import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { HomeEngineer } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { addByHandPath } from "../../../app/paths";
import { useLive } from "../../../live/store";
import { Card, EmptyState } from "../../../ui";
import { useArrivals } from "../hooks";
import { buildNavModel } from "../model/nav";
import { mergeFeed } from "../model/people";
import { Backlog } from "../parts/Backlog";
import { CounterRow, liveCounterItems } from "../parts/CounterRow";
import { DeliverableTiles, goalForTile } from "../parts/DeliverableTiles";
import { FilesLine } from "../parts/FilesLine";
import { GOAL_INPUT_ID, GoalBox } from "../parts/GoalBox";
import { HomeMiniMap } from "../parts/HomeMiniMap";
import { LiveFeed } from "../parts/LiveFeed";
import { NavigatorList } from "../parts/NavigatorList";
import { WelcomeCard } from "../parts/WelcomeCard";
import { WhatsNewList } from "../parts/WhatsNewList";
import { WorkingNow } from "../parts/WorkingNow";
import { navigatorHref } from "../model/words";
import { useOpenSet } from "../useOpenSet";

/**
 * The engineer's home (screen 2): a welcome back, the goal box, the family tree with its rings, what is new, tiles
 * to start from each target document, the item backlog, who is working on what, and the live map and feed.
 */
export function EngineerHome({ home, canStart }: { home: HomeEngineer; canStart: boolean }) {
  const me = useCurrentMe();
  const live = useLive();
  const [goal, setGoal] = useState("");
  const model = useMemo(() => buildNavModel(home.tree), [home.tree]);
  const { expanded, toggle } = useOpenSet(model, null);
  const nodeIds = useMemo(() => model.nodes.map((node) => node.id), [model]);
  const arrivals = useArrivals(nodeIds);
  // The libraries are always loaded, so the tree is never empty. The team's own work starts with its first family or item.
  const noFamilyYet = model.nodes.length > 0 && !model.nodes.some((node) => node.kind === "family" || node.kind === "item");

  const feed = useMemo(() => mergeFeed(home.live_feed, live.recent), [home.live_feed, live.recent]);
  const feedIds = useMemo(() => feed.map((notice) => String(notice.number)), [feed]);
  const feedArrivals = useArrivals(feedIds);

  // The live channel has the newest list of who is online; until it has spoken the home answer is used.
  const presence = live.connected || live.presence.length > 0 ? live.presence : home.welcome.working;
  const ownMembership = me.active?.membership_id;

  return (
    <div className="nv-home stack">
      <WelcomeCard welcome={home.welcome} displayName={me.person.display_name} firstVisit={home.whats_new.first_visit} />
      {canStart && <GoalBox goal={goal} onGoalChange={setGoal} suggestions={home.goal_suggestions} />}
      <CounterRow items={liveCounterItems(home.counters)} />
      <FilesLine status={home.files} />
      <div className="nv-grid">
        <Card aria-labelledby="tree-title" className="nv-grid__wide">
          <div className="nv-card__head">
            <h2 id="tree-title">The family tree</h2>
            <span className="row">
              {canStart && <Link to={addByHandPath()}>Add an element</Link>}
              <Link to="/navigate">Open the family navigator</Link>
            </span>
          </div>
          {model.nodes.length === 0 ? (
            <EmptyState title="The family tree is empty" action={canStart ? <Link to={addByHandPath()}>Add an element by hand</Link> : undefined}>
              Libraries, families and items appear here as soon as your team adds them.
            </EmptyState>
          ) : (
            <>
              {noFamilyYet && (
                <p className="nv-home__first muted">
                  No family or item has been added yet. {canStart ? <Link to={addByHandPath({ kind: "family" })}>Add the first family</Link> : "Your team adds the first family."}
                </p>
              )}
              <NavigatorList
                model={model}
                expanded={expanded}
                onToggle={toggle}
                label="Family tree"
                hrefFor={(node) => (node.kind === "item" ? `/items/${encodeURIComponent(node.id)}/map` : navigatorHref(node.id))}
                arrivals={arrivals}
              />
            </>
          )}
        </Card>
        <div className="nv-grid__side stack">
          <Card aria-labelledby="news-title" className="nv-newscard">
            <div className="nv-card__head">
              <h2 id="news-title">What is new</h2>
              <Link to="/whats-new">Open what's new</Link>
            </div>
            <WhatsNewList news={home.whats_new} mode="panel" />
          </Card>
          <WorkingNow entries={presence} ownMembershipId={ownMembership} nameOf={(id) => model.byId.get(id)?.label} />
        </div>
      </div>
      <DeliverableTiles
        tiles={home.deliverable_tiles}
        onPick={(tile) => {
          setGoal(goalForTile(tile));
          document.getElementById(GOAL_INPUT_ID)?.focus();
        }}
      />
      <div className="nv-grid">
        <div className="nv-grid__wide">
          <Backlog backlog={home.backlog} canStart={canStart} />
        </div>
        <div className="nv-grid__side stack">
          <HomeMiniMap model={model} arrivals={arrivals} />
          <LiveFeed notices={feed} arrivals={feedArrivals} />
        </div>
      </div>
    </div>
  );
}
